import './image_cropper.less';
import 'cropperjs/dist/cropper.css';

import React, { Component } from 'react';
import autobind from 'class-autobind';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import min from 'lodash/min';
import max from 'lodash/max';

import CropperJsWrapper from 'zoomforth_wrappers/cropperjs/cropperjs.jsx';
import Slider from 'zoomforth_component_lib/slider.jsx';
import Button from 'zoomforth_codesbane_editor/component_lib/button.jsx';
import SimpleModalWithHeader from 'zoomforth_component_lib/simple_modal_with_header.jsx';
import TimedTextInput from 'zoomforth_component_lib/timed_inputs/text.jsx';
import Checkbox from 'zoomforth_component_lib/checkbox.jsx';
import { zfImagePropType } from 'zoomforth_codesbane_viewer/lib/prop_types.js';
import { LoadingIcon } from 'zoomforth_component_lib/app_modern_icons.jsx';

import {
  zfImageOrLegacyCropDataToState,
  stateToZfImage
} from './transformers.js';

export const ASPECT_RATIO_SQUARE = 1;

const SLIDER_BORDER_RADIUS_PERCENT_MIN = 0;
const SLIDER_BORDER_RADIUS_PERCENT_MAX = 50;
const SLIDER_BORDER_RADIUS_PERCENT_MARKS = {};

const SLIDER_ZOOM_PERCENT_MIN = 10;
const SLIDER_ZOOM_PERCENT_MAX = 300;
const SLIDER_ZOOM_PERCENT_MARKS = { 100: true };

const FREE_ASPECT_RATIO = 0;

// cropperjs constrains a locked aspect ratio exactly, so any meaningful
// difference here means the saved crop was made with Custom size enabled.
const ASPECT_RATIO_MATCH_TOLERANCE = 0.01;

function wasCroppedWithCustomAspectRatio(width, height, lockedAspectRatio) {
  if (!width || !height || !lockedAspectRatio) {
    return false;
  }

  return Math.abs((width / height) - lockedAspectRatio) > ASPECT_RATIO_MATCH_TOLERANCE;
}

const sliderStyle = {
  railStyle: { backgroundColor: '#626364' },
  trackStyle: { backgroundColor: '#4282D5' },
  dotStyle: {
    borderColor: '#626364',
    backgroundColor: '#626364',
  },
  activeDotStyle: {
    borderColor: '#4282D5',
    backgroundColor: '#4282D5',
  },
  handleStyle: {
    borderColor: '#4282D5',
    backgroundColor: '#4282D5',
  }
};

function formatNumber(number, options = {}) {
  var handledNumber = Number(number);

  if (isNaN(handledNumber)) {
    if (options.default != null) {
      return options.default;
    }
    return 0;
  }

  if (options.min != null) {
    handledNumber = max([handledNumber, options.min]);
  }
  if (options.max != null) {
    handledNumber = min([handledNumber, options.max]);
  }

  return handledNumber;
}


class ImageCropper extends Component {

  constructor(props) {
    super(props);

    const state = zfImageOrLegacyCropDataToState(
      this.props.zfImage,
      this.props.legacyCropData,
      this.props.legacyImageSrc
    );

    const lockedAspectRatio = this.props.aspectRatio || FREE_ASPECT_RATIO;

    // The saved crop itself doesn't record whether Custom size was used, but if its
    // width/height don't match the locked aspect ratio, it could only have been produced
    // with Custom size enabled (a locked ratio is enforced exactly by cropperjs), so we can
    // restore that state from the existing crop data instead of persisting a new flag.
    const initialAspectRatio = (
      this.props.allowFreeAspectRatio
      && wasCroppedWithCustomAspectRatio(state.width, state.height, lockedAspectRatio)
    )
      ? FREE_ASPECT_RATIO
      : lockedAspectRatio;

    this.state = {
      ...state,
      aspectRatio: initialAspectRatio,
      initX: state.x,
      initY: state.y,
      initWidth: state.width,
      initHeight: state.height,
      isLoading: true,
      hasFinishedInitializing: false,
      lockedAspectRatio
    };

    this.previewRefs = [];

    autobind(this);
  }

  handlePreviewRef(previewRef) {
    this.previewRefs.push(previewRef);
  }

  handleCropImage() {
    this.props.onCrop(stateToZfImage(this.state));
  }

  handleCropperUpdate(newState) {
    this.setState(newState);
  }

  handleToggleFreeAspectRatio(isFree) {
    this.setState({
      aspectRatio: isFree ? FREE_ASPECT_RATIO : this.state.lockedAspectRatio
    });
  }

  handleOnReady(){
    this.setState({ 
      isLoading: false, 
      hasFinishedInitializing: true 
    });
  }

  render() {
    const {
      isBorderRadiusDisabled,
      allowFreeAspectRatio,
      onCancel
    } = this.props;

    const {
      originalImageSrc,
      originalImageWidth,
      originalImageHeight,
      x,
      y,
      width,
      height,
      initX,
      initY,
      initWidth,
      initHeight,
      borderRadius,
      aspectRatio,
      zoom,
      isLoading,
      hasFinishedInitializing,
      isOutOfBoundsAllowed
    } = this.state;

    return (
      <SimpleModalWithHeader {...{
        className: 'image-cropper-modal',
        title: 'Crop Image',
        show: true,
        onRequestClose: onCancel
      }}>
        <div className={classnames('image-cropper-container')}>
          <div className={classnames('cropper')}>
            { isLoading && (
              <div className="loader-wrapper">
                <div className="loader">
                  <LoadingIcon />
                </div>
              </div> 
            )}
            <CropperJsWrapper {...{
              className: classnames(isLoading && 'hidden'),
              originalImageSrc,
              originalImageWidth,
              originalImageHeight,
              x, 
              y,
              width,
              height,
              initX,
              initY,
              initWidth,
              initHeight,
              zoom,
              borderRadius,
              aspectRatio,
              previewRefs: this.previewRefs,
              isOutOfBoundsAllowed,
              hasFinishedInitializing,
              hasCompressionChanged: this.props.hasCompressionChanged,
              onCropperUpdate: this.handleCropperUpdate,
              onReady: this.handleOnReady
            }} />
          </div>

          <div className="sidebar">

            <div className="preview-wrapper">
              <div
                ref={this.handlePreviewRef}
                className="preview"
                style={{ borderRadius: `${borderRadius}%` }}
              />
            </div>

            <div className="controls">
              <div className="header-row row">
                <span className="control-label">
                  Size
                </span>
              </div>

              { allowFreeAspectRatio && (
                <div className="row">
                  <Checkbox
                    label="Custom size (any width or height)"
                    checked={aspectRatio === FREE_ASPECT_RATIO}
                    onChange={(e) => this.handleToggleFreeAspectRatio(e.target.checked)}
                  />
                </div>
              )}

              <div className="row">
                <span className="col">
                  <div className="control-label">W</div>

                  <TimedTextInput
                    className="number-input"
                    name="Width"
                    value={Math.round(formatNumber(width))}
                    onFocus={(event) => event.target.select()}
                    onChange={(e) => {
                      this.setState({
                        width: formatNumber(e.target.value, { default: 100 })
                      });
                    }}
                  />

                  <span className="control-unit">px</span>
                </span>

                <span className="col">
                  <span className="control-label">H</span>

                  <TimedTextInput
                    className="number-input"
                    name="Height"
                    value={Math.round(formatNumber(height))}
                    onFocus={(event) => event.target.select()}
                    onChange={(e) => {
                      this.setState({
                        height: formatNumber(e.target.value, { default: 100 })
                      });
                    }}
                  />

                  <span className="control-unit">px</span>
                </span>
              </div>

              <div className="header-row row">
                <span className="control-label">
                  Position
                </span>
              </div>

              <div className="row">
                <span className="col">
                  <span className="control-label">X</span>

                  <TimedTextInput
                    className="number-input"
                    value={Math.round(formatNumber(x))}
                    onFocus={(event) => event.target.select()}
                    onChange={(e) => {
                      this.setState({
                        x: formatNumber(e.target.value, { default: 0 })
                      });
                    }}
                  />

                  <span className="control-unit">px</span>
                </span>

                <span className="col">
                  <span className="control-label">Y</span>

                  <TimedTextInput
                    className="number-input"
                    value={Math.round(formatNumber(y))}
                    onFocus={(event) => event.target.select()}
                    onChange={(e) => {
                      this.setState({
                        y: formatNumber(e.target.value, { default: 0 })
                      });
                    }}
                  />

                  <span className="control-unit">px</span>
                </span>
              </div>

              <div className="header-row row">
                <span className="control-label">
                  Border Radius
                </span>
              </div>

              <div className="row">
                <Slider {...{
                  ...sliderStyle,
                  disabled: isBorderRadiusDisabled,
                  min: SLIDER_BORDER_RADIUS_PERCENT_MIN,
                  max: SLIDER_BORDER_RADIUS_PERCENT_MAX,
                  marks: SLIDER_BORDER_RADIUS_PERCENT_MARKS,
                  step: 0.5,
                  defaultValue: borderRadius,
                  value: borderRadius,
                  onChange: (borderRadius) => this.setState({ borderRadius })
                }}

                />
                <TimedTextInput
                  className="number-input"
                  value={formatNumber(borderRadius) * 2}
                  disabled={isBorderRadiusDisabled}
                  onFocus={(event) => event.target.select()}
                  onChange={(e) => this.setState({
                    borderRadius: formatNumber(
                      e.target.value,
                      {
                        min: SLIDER_BORDER_RADIUS_PERCENT_MIN,
                        max: SLIDER_BORDER_RADIUS_PERCENT_MAX * 2,
                        default: SLIDER_BORDER_RADIUS_PERCENT_MIN
                      }
                    ) / 2
                  })}
                />

                <span className="control-unit">%</span>
              </div>

              <div className="header-row row">
                <span className="control-label">
                  Zoom
                </span>
              </div>

              <div className="row">
                <Slider {...{
                  ...sliderStyle,
                  min: SLIDER_ZOOM_PERCENT_MIN,
                  max: SLIDER_ZOOM_PERCENT_MAX,
                  marks: SLIDER_ZOOM_PERCENT_MARKS,
                  step: 5,
                  value: zoom,
                  onChange: (zoom) => {
                    this.setState({ zoom });
                  }
                }}
                />

                <TimedTextInput
                  className="number-input"
                  value={Math.round(formatNumber(zoom))}
                  onFocus={(event) => event.target.select()}
                  onChange={(e) => {
                    this.setState({
                      zoom: formatNumber(e.target.value, { min: 5, default: 100 })
                    });
                  }}
                />

                <span className="control-unit">%</span>
              </div>

              <div className="row">
                <Checkbox
                  label="Allow out of bounds"
                  checked={isOutOfBoundsAllowed}
                  onChange={(e) => {
                    this.setState({
                      isOutOfBoundsAllowed: e.target.checked
                    });
                  }}
                />
              </div>
            </div>

            <div className="sidebar-buttons">
              <Button type="secondary" onClick={onCancel}>
                Cancel
              </Button>

              <Button type="primary" onClick={this.handleCropImage}>
                Crop
              </Button>
            </div>
          </div>
        </div>
      </SimpleModalWithHeader>
    );
  }
}

ImageCropper.defaultProps = {
  borderRadius: 0,
  isBorderRadiusDisabled: false,
  aspectRatio: null,
  allowFreeAspectRatio: false
};

ImageCropper.propTypes = {
  zfImage: zfImagePropType,
  legacyCropData: PropTypes.object,
  aspectRatio: PropTypes.number,
  allowFreeAspectRatio: PropTypes.bool,
  isBorderRadiusDisabled: PropTypes.bool,
  hasCompressionChanged: PropTypes.bool,
  onCrop: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onCropFail: PropTypes.func.isRequired
};

export default ImageCropper;
